import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { SelectedCellsInfoDocument } from 'generated/resources/graphql'
import { flatten, isEmpty, uniq } from 'lodash'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { FormatTypes, convertDate, dateToString } from 'modules/core/lib/dates'

import NamesToDisplay from 'modules/shift_management/components/ShiftManagement/NamesToDisplay'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'

export interface Props {
  employeeIds?: string[]
  date?: moment.Moment
  showDates: boolean
}

const SelectedCellsInfo = ({ employeeIds = [], date, showDates }: Props) => {
  const [{ view }] = useSchedule()
  const { daysByRow } = useSelectionState()
  const { company } = useCurrentSession()
  const selectedEmployeeIds = isEmpty(employeeIds) ? Object.keys(daysByRow) : employeeIds

  const { data } = useQuery(SelectedCellsInfoDocument, {
    variables: {
      companyId: company.id,
      ids: selectedEmployeeIds,
    },
    skip: !view.isByEmployees,
  })

  const employeeNames = data?.employees.employeeNames ?? []
  const userNames = employeeNames.map((employee) => employee.fullName)

  const sortedNames = userNames.sort((a, b) => a.localeCompare(b))

  const selectionDates = uniq(flatten(Object.values(daysByRow))).sort((a, b) =>
    moment(a).diff(moment(b))
  )
  const selectionStartDate = selectionDates[0]
  const selectionEndDate = selectionDates[selectionDates.length - 1]

  const dateFormat: FormatTypes = view.isDaily ? 'ui_short_with_time' : 'backend'
  const convertedSelectionStartDate = selectionStartDate
    ? convertDate(selectionStartDate, dateFormat, 'ui_long')
    : ''
  const convertedSelectionEndDate = selectionEndDate
    ? convertDate(selectionEndDate, dateFormat, 'ui_long')
    : ''

  const startDate = date ? dateToString(date, 'ui_long') : convertedSelectionStartDate
  const endDate = date ? dateToString(date, 'ui_long') : convertedSelectionEndDate

  const sameDate = startDate === endDate

  const singleDay = selectionStartDate
    ? convertDate(selectionStartDate, dateFormat, 'ui_long_with_year')
    : ''
  const start = selectionStartDate
    ? convertDate(selectionStartDate, dateFormat, 'ui_abbreviated_month_year')
    : ''
  const end = selectionEndDate
    ? convertDate(selectionEndDate, dateFormat, 'ui_abbreviated_month_year')
    : ''

  const periodExplanation = sameDate ? singleDay : `${start} - ${end}`

  return (
    <Box
      borderRadius={{ all: 'abs008' }}
      padding="s16"
      gap="s12"
      border={{
        all: {
          style: 'solid',
          width: 's1',
          color: 'grey400',
        },
      }}
      background="grey100"
    >
      {view.isByEmployees && (
        <Box flexDirection="row" gap="s8">
          <Box>
            <Icon.Medium icon="person" color="grey700" />
          </Box>
          <Box>
            <NamesToDisplay items={sortedNames} maxItemsToDisplay={4} />
          </Box>
        </Box>
      )}
      {showDates && (
        <Box flexDirection="row" gap="s8">
          <Box>
            <Icon.Medium icon="calendar" color="grey700" />
          </Box>
          <Box>
            <Text.Normal size="300" color="grey1000">
              {periodExplanation}
            </Text.Normal>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default observer(SelectedCellsInfo)
